import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useCurrentUser } from '../queries/useCurrentUser'
import { Modal, Content, Button, Indicators, Card } from 'ui'
import { ProfileCompletionCard } from '../components/ProfileCompletionCard'

export const RequireApproval: React.FC<{ children: JSX.Element }> = ({
  children,
}) => {
  const { data: user, isLoading } = useCurrentUser()
  const navigate = useNavigate()

  // Handle loading state
  if (isLoading) {
    return <Indicators.FullScreen />
  }

  // Block if profile is incomplete (PENDING_COMPLETION status)
  const isIncomplete = user?.status === 'PENDING_COMPLETION'

  // Block if professional awaiting approval (NEEDS_APPROVAL status)
  const needsApproval =
    user?.userRole === 'PROFESSIONAL' && user?.status === 'NEEDS_APPROVAL'

  const handleLogout = () => {
    navigate('/app/logout')
  }

  const handleCompleteProfile = () => {
    const path =
      user?.userRole === 'EDUCATOR'
        ? '/onboard/educator/start'
        : '/onboard/professional/start'
    navigate(path)
  }

  // Show lockout modal for incomplete profiles
  if (isIncomplete) {
    return (
      <Modal.Container transparent={true}>
        <Modal.ContentRegion
          size={Modal.Size.medium}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          <Card.View>
            <div className="p-8">
              <div className="text-center">
                <Content.Heading className="mb-4">
                  Complete Your Profile
                </Content.Heading>

                <Content.Body size="large" className="mb-4">
                  Please complete your profile to access the platform.
                </Content.Body>

                <Content.Body size="small" className="text-gray-600 mb-6">
                  Complete all required fields to unlock full access to EDU PRO
                  Connect.
                </Content.Body>
              </div>

              <ProfileCompletionCard profile={user} className="mb-6" />

              <div className="flex flex-col gap-3">
                <Button
                  onClick={handleLogout}
                  className="w-full"
                  variant="outline"
                >
                  Log Out
                </Button>
              </div>
            </div>
          </Card.View>
        </Modal.ContentRegion>
      </Modal.Container>
    )
  }

  // Show lockout modal for professionals awaiting approval
  if (needsApproval) {
    return (
      <Modal.Container transparent={true}>
        <Modal.ContentRegion
          size={Modal.Size.medium}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          <Card.View>
            <div className="p-8 text-center">
              <Content.Heading className="mb-4">
                Profile Approval Required
              </Content.Heading>

              <Content.Body size="large" className="mb-4">
                Your profile is complete and has been submitted for review. Our
                team will review your profile and approve it shortly. You'll be
                notified when your profile is approved.
              </Content.Body>

              <Content.Body size="small" className="text-gray-600 mb-6">
                Thank you for your patience.
              </Content.Body>

              <div className="flex flex-col gap-3 mt-6">
                <Button
                  onClick={handleLogout}
                  className="w-full"
                  variant="outline"
                >
                  Log Out
                </Button>
              </div>
            </div>
          </Card.View>
        </Modal.ContentRegion>
      </Modal.Container>
    )
  }

  // User is approved or not blocked - allow access
  return children
}
